/**
 * AmbientOverlayScene — persistent foreground overlay (AGE-1431, painted-
 * transitions phase α).
 *
 * A parallel scene that runs ABOVE the active gameplay scene at all times
 * and BELOW UIScene. It carries straight through every transition without
 * being stopped or restarted, so the player's eye latches onto its drift
 * texture and reads scene swaps as a continuous experience instead of a
 * cut. See `docs/design/scene-transitions-painted.md` §4.D.
 *
 * Contents:
 *   - A tiling watercolor-grain `TileSprite` filling the canvas at ~5%
 *     alpha. Slowly drifts horizontally for a subliminal parallax cue.
 *   - A particle emitter spawning ~16 soft motes that drift upward and
 *     gently sideways. Tints drawn from the art-bible palette.
 *
 * Lifecycle:
 *   - Launched once at PreloaderScene completion via `ensureLaunched()`.
 *     Subsequent calls are no-ops; the scene is never `stop()`-ed across
 *     any transition (portal, sleep, faint, scene swap).
 *   - Listens for `prefers-reduced-motion`; dampens drift speeds to 25%
 *     when the preference is set, and re-tunes live if it changes.
 *
 * Performance:
 *   - 16 motes via the standard Phaser 4 `add.particles` API. No shaders.
 *   - Procedurally generated 256×256 grain texture (one-time graphics
 *     paint) — no asset load.
 *   - Procedurally generated 24×24 mote texture (one-time graphics
 *     paint).
 */

import * as Phaser from 'phaser';
import { devLog } from '../../debug/devLog';
import { isWebGL } from '../../rendering/rendererUtils';
import { AMBIENT_OVERLAY_SCENE_KEY } from '../sceneKeys';
import {
  AMBIENT_GRAIN_ALPHA,
  AMBIENT_GRAIN_DRIFT_PX_PER_S,
  AMBIENT_MOTE_ALPHA_MAX,
  AMBIENT_MOTE_ALPHA_MIN,
  AMBIENT_MOTE_COUNT,
  AMBIENT_MOTE_LIFESPAN_MS,
  AMBIENT_MOTE_SPEED_X_MAX,
  AMBIENT_MOTE_SPEED_X_MIN,
  AMBIENT_MOTE_SPEED_Y_MAX,
  AMBIENT_MOTE_SPEED_Y_MIN,
  AMBIENT_MOTE_TINTS,
  dampenForReducedMotion,
  getReducedMotionQuery,
  readPrefersReducedMotion,
} from './ambientOverlayState';

/** Texture key for the procedural mote sprite. */
const MOTE_TEXTURE_KEY = '__ambient_overlay_mote__';
/** Texture key for the procedural watercolor-grain tile. */
const GRAIN_TEXTURE_KEY = '__ambient_overlay_grain__';

/** Side length of the mote texture in px. */
const MOTE_TEXTURE_SIZE = 24;
/** Side length of the grain texture in px (tiled across the canvas). */
const GRAIN_TEXTURE_SIZE = 256;

/**
 * Persistent ambient-overlay scene. Constructor takes no init data; the
 * scene is configured entirely in `create()` and re-tunes itself when
 * the reduced-motion preference changes.
 */
export class AmbientOverlayScene extends Phaser.Scene {
  public static readonly KEY = AMBIENT_OVERLAY_SCENE_KEY;

  private grainTile: Phaser.GameObjects.TileSprite | null = null;
  private moteEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;

  /** Live media-query for reduced motion. Null in headless environments. */
  private reducedMotionQuery: ReturnType<typeof getReducedMotionQuery> = null;
  /** Listener kept on the instance so we can detach in shutdown(). */
  private readonly handleReducedMotionChange = (ev: { matches: boolean }): void => {
    this.applyReducedMotion(ev.matches);
  };

  /** Most-recent reduced-motion state. Tracked so `applyReducedMotion`
   *  is idempotent — no point re-tuning if nothing changed. */
  private reducedMotionActive = false;

  constructor() {
    super({ key: AMBIENT_OVERLAY_SCENE_KEY });
  }

  /**
   * Idempotent launcher. Call from PreloaderScene once asset loading is
   * complete and the title flow is about to start. Subsequent calls are
   * no-ops; the scene is never restarted across transitions.
   */
  public static ensureLaunched(scene: Phaser.Scene): void {
    if (scene.scene.isActive(AmbientOverlayScene.KEY)) return;
    scene.scene.launch(AmbientOverlayScene.KEY);
  }

  create(): void {
    devLog('[AmbientOverlayScene] create');

    this.installGrainTexture();
    this.installMoteTexture();

    const reduced = readPrefersReducedMotion();
    this.reducedMotionActive = reduced;

    this.buildGrainLayer(reduced);
    this.buildMoteEmitter(reduced);

    this.attachReducedMotionListener();

    // Stop the camera from tinting our drift texture with the gameplay
    // scene's clear colour: a transparent clear keeps the underlying
    // gameplay scene visible through this scene's empty pixels.
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.detachReducedMotionListener();
      this.grainTile = null;
      this.moteEmitter = null;
    });
  }

  /**
   * Per-frame grain drift. Phaser's TileSprite supports `tilePositionX`
   * for a parallax-like scroll without moving the GameObject itself, which
   * keeps the rectangle pinned to the screen.
   */
  update(_time: number, delta: number): void {
    if (!this.grainTile) return;
    const driftPxPerS = dampenForReducedMotion(
      AMBIENT_GRAIN_DRIFT_PX_PER_S,
      this.reducedMotionActive,
    );
    this.grainTile.tilePositionX += (driftPxPerS * delta) / 1000;
  }

  // ── reduced-motion plumbing ──────────────────────────────────────────────

  private attachReducedMotionListener(): void {
    this.reducedMotionQuery = getReducedMotionQuery();
    this.reducedMotionQuery?.addEventListener?.(
      'change',
      this.handleReducedMotionChange,
    );
  }

  private detachReducedMotionListener(): void {
    this.reducedMotionQuery?.removeEventListener?.(
      'change',
      this.handleReducedMotionChange,
    );
    this.reducedMotionQuery = null;
  }

  private applyReducedMotion(reduced: boolean): void {
    if (this.reducedMotionActive === reduced) return;
    this.reducedMotionActive = reduced;
    if (this.moteEmitter) {
      this.moteEmitter.setConfig({
        speedX: {
          min: dampenForReducedMotion(AMBIENT_MOTE_SPEED_X_MIN, reduced),
          max: dampenForReducedMotion(AMBIENT_MOTE_SPEED_X_MAX, reduced),
        },
        speedY: {
          min: dampenForReducedMotion(AMBIENT_MOTE_SPEED_Y_MIN, reduced),
          max: dampenForReducedMotion(AMBIENT_MOTE_SPEED_Y_MAX, reduced),
        },
      });
    }
  }

  // ── grain layer ──────────────────────────────────────────────────────────

  private buildGrainLayer(reduced: boolean): void {
    const w = this.scale.width;
    const h = this.scale.height;
    this.grainTile = this.add
      .tileSprite(0, 0, w, h, GRAIN_TEXTURE_KEY)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setAlpha(AMBIENT_GRAIN_ALPHA)
      .setDepth(0);
    // No early-return here: even with reduced motion we keep the grain
    // visible — the spec says "grain is static" under reduced motion, and
    // the tilePosition update guards itself with the dampening factor so
    // the value keeps drifting at 25% (1 px/s — perceptually still).
    void reduced;
  }

  /**
   * Build a 256×256 watercolor-paper noise tile procedurally. We sample
   * a soft warm-cream base and stipple it with thousands of tiny cream/
   * linen/sage flecks at low alpha so the texture reads as paper grain
   * rather than digital noise.
   */
  private installGrainTexture(): void {
    if (this.textures.exists(GRAIN_TEXTURE_KEY)) return;

    const gfx = this.make.graphics({ x: 0, y: 0 }, false);

    // Warm parchment base — stays muted at 5% scene alpha so it doesn't
    // wash painted plates out.
    gfx.fillStyle(0xead9c4, 1);
    gfx.fillRect(0, 0, GRAIN_TEXTURE_SIZE, GRAIN_TEXTURE_SIZE);

    // Stipple with palette flecks. Deterministic LCG so the grain is the
    // same every boot — players don't see the seed change between sessions.
    let rng = 0x9e3779b9;
    const next = (): number => {
      rng = (rng * 1664525 + 1013904223) >>> 0;
      return rng / 0x100000000;
    };
    const fleckTints = [0xf5e6d0, 0xc8d4a9, 0xd9c8ae, 0x8b7355];
    const fleckCount = 1400;
    for (let i = 0; i < fleckCount; i++) {
      const tint = fleckTints[Math.floor(next() * fleckTints.length)] ?? 0xf5e6d0;
      const alpha = 0.05 + next() * 0.18;
      gfx.fillStyle(tint, alpha);
      const x = Math.floor(next() * GRAIN_TEXTURE_SIZE);
      const y = Math.floor(next() * GRAIN_TEXTURE_SIZE);
      // 1×1 flecks read as paper grain. A handful of 2×2 patches give the
      // watercolor "wet-on-dry" feel without procedural noise smell.
      const size = next() < 0.08 ? 2 : 1;
      gfx.fillRect(x, y, size, size);
    }

    gfx.generateTexture(GRAIN_TEXTURE_KEY, GRAIN_TEXTURE_SIZE, GRAIN_TEXTURE_SIZE);
    gfx.destroy();
  }

  // ── mote emitter ─────────────────────────────────────────────────────────

  private buildMoteEmitter(reduced: boolean): void {
    if (!isWebGL(this)) {
      // Canvas fallback: skip the emitter. The grain plate alone still
      // anchors the eye between scene swaps.
      return;
    }

    const w = this.scale.width;
    const h = this.scale.height;

    this.moteEmitter = this.add.particles(0, 0, MOTE_TEXTURE_KEY, {
      // Spawn anywhere across the canvas. Motes that originate near the
      // top of the canvas drift off the edge quickly and respawn somewhere
      // along the bottom band thanks to the lifespan cycle.
      x: { min: 0, max: w },
      y: { min: h * 0.4, max: h * 1.05 },
      speedX: {
        min: dampenForReducedMotion(AMBIENT_MOTE_SPEED_X_MIN, reduced),
        max: dampenForReducedMotion(AMBIENT_MOTE_SPEED_X_MAX, reduced),
      },
      speedY: {
        min: dampenForReducedMotion(AMBIENT_MOTE_SPEED_Y_MIN, reduced),
        max: dampenForReducedMotion(AMBIENT_MOTE_SPEED_Y_MAX, reduced),
      },
      alpha: { min: AMBIENT_MOTE_ALPHA_MIN, max: AMBIENT_MOTE_ALPHA_MAX },
      scale: { min: 0.6, max: 1.4 },
      rotate: { min: 0, max: 360 },
      lifespan: AMBIENT_MOTE_LIFESPAN_MS,
      frequency: AMBIENT_MOTE_LIFESPAN_MS / AMBIENT_MOTE_COUNT,
      quantity: 1,
      maxParticles: 0, // 0 = unlimited cap — the emitter rate caps the cluster
      tint: [...AMBIENT_MOTE_TINTS] as number[],
      blendMode: Phaser.BlendModes.NORMAL,
    });
    this.moteEmitter.setScrollFactor(0);
    this.moteEmitter.setDepth(1);

    // Pre-fill the emitter so the very first frame after launch already
    // shows a populated cluster instead of a slow ramp-up.
    this.moteEmitter.emitParticle(AMBIENT_MOTE_COUNT);
  }

  /**
   * Generate a soft circular mote sprite (~24px) procedurally. Same
   * radial-gradient pattern AmbientParticleSystem uses for embers — gives
   * the cluster a painterly soft edge without a Wave-2 atlas.
   */
  private installMoteTexture(): void {
    if (this.textures.exists(MOTE_TEXTURE_KEY)) return;

    const SIZE = MOTE_TEXTURE_SIZE;
    const HALF = SIZE / 2;
    const gfx = this.make.graphics({ x: 0, y: 0 }, false);

    for (let r = HALF; r >= 1; r--) {
      const alpha = (1 - r / HALF) ** 2;
      gfx.fillStyle(0xffffff, alpha);
      gfx.fillCircle(HALF, HALF, r);
    }

    gfx.generateTexture(MOTE_TEXTURE_KEY, SIZE, SIZE);
    gfx.destroy();
  }
}
